import {
    Body_14_Coral_Primary_Regular_14,
    Body_16_Coral_Emphasis_16,
    Body_16_Coral_Strong_emphasis_16, Title_20_Coral_Primary_Regular_20, Title_40_Coral_emphasis_40
} from "@UI/typography/generated/typography.tsx";
import {Gt, GtCurrency, GtDatetime, LocalDTFormater} from "@src/i18n/Gt.tsx";
import {MenuLinkButton} from "@src/CommonComponents/MenuLinkButton/MenuLinkButton.tsx";
import {useCallback, useMemo, useEffect, useRef, useState} from "react";
import {SalaryIco} from "@UI/components/icons/SalaryIco.tsx";
import {Stack} from "@src/CommonComponents/Stack/Stack.tsx";
import {useQuery} from "@tanstack/react-query";
import {apiClient} from "@src/httpClient/api.ts";
import {useCredentialsStore} from "@src/Store/userStore.ts";
import {Button, ButtonType} from "@UI/components/controls/Button/Button.tsx";
import {StatementList as StatementRow} from "@src/httpClient/__generated__/Api.ts";
import styles from "./statementList.module.less";
import {useNavigate, useSearchParams} from "react-router-dom";
import cn from "classnames";
import {ChevronLeft} from "@UI/components/icons/ChevronLeft.tsx";
import {ChevronRight} from "@UI/components/icons/ChevronRight.tsx";
import {useTranslation} from "react-i18next";
import {BookOpen} from "@UI/components/icons/BookOpen.tsx";


const PAGE_LINK = "/settings/salary/"
const PAGE_SIZE = 10

const MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] as const

export const StatementsMenuButton = ({noPadding = true}: { noPadding?: boolean }) => {
    if (!noPadding) {
        return (
            <MenuLinkButton href={PAGE_LINK} icon={<SalaryIco size={22}/>}>
                <Body_16_Coral_Emphasis_16>
                    <Gt>My salary</Gt>
                </Body_16_Coral_Emphasis_16>
            </MenuLinkButton>
        )
    }
    return (
        <MenuLinkButton href={PAGE_LINK} icon={<SalaryIco size={22}/>} style={{padding: 0}}>
            <Body_16_Coral_Emphasis_16>
                <Gt>My salary</Gt>
            </Body_16_Coral_Emphasis_16>
        </MenuLinkButton>
    )
}

// Statement periods are a year and a month; any day of the month formats the same.
const periodDate = (statement: StatementRow) =>
    `${statement.period_year}-${String(statement.period_month).padStart(2, '0')}-01`

const getMonthName = (month: number, lng: string) => {
    const date = new Date(2024, month - 1, 1)
    return new Intl.DateTimeFormat(lng, {month: 'short'}).format(date)
}

const MonthDropdown = ({activeYear, activeMonth, onSelect}: {
    activeYear: string,
    activeMonth?: string,
    onSelect: (month?: string) => void,
}) => {
    const {i18n} = useTranslation()
    const ref = useRef<HTMLDivElement>(null)

    return (
        <div ref={ref} className={styles.monthDropdown}>
            <Stack gap={4} className={styles.monthButtons}>
                {MONTHS.map(m => (
                    <Button
                        key={m}
                        type={ButtonType.third}
                        className={cn(styles.monthButton, {[styles.monthButtonActive]: activeMonth === String(m)})}
                        onClick={() => onSelect(activeMonth === String(m) ? undefined : String(m))}
                    >
                        {getMonthName(m, i18n.language)}
                    </Button>
                ))}
            </Stack>
        </div>
    )
}

const YearMonthFilters = () => {
    const [params] = useSearchParams()
    const navigate = useNavigate()
    const [showMonths, setShowMonths] = useState(false)
    const wrapperRef = useRef<HTMLDivElement>(null)

    const now = new Date()
    const currentYear = now.getFullYear()
    const prevYear = currentYear - 1

    const activeYear = useMemo(() => params.get("year") ?? String(currentYear), [params])
    const activeMonth = useMemo(() => params.get("month") ?? undefined, [params])

    const applyFilter = useCallback((year: string, month?: string) => {
        const p = new URLSearchParams(params)
        p.set("year", year)
        if (month) p.set("month", month)
        else p.delete("month")
        p.delete("page")
        navigate(`?${p.toString()}`, {replace: true})
    }, [params])

    useEffect(() => {
        if (!showMonths) return
        const handleClick = (e: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
                setShowMonths(false)
            }
        }
        document.addEventListener('mousedown', handleClick)
        return () => document.removeEventListener('mousedown', handleClick)
    }, [showMonths])

    return (
        <div ref={wrapperRef} className={styles.yearMonthWrapper}>
            <Stack gap={8} className={styles.yearButtons}>
                <Button
                    type={ButtonType.third}
                    className={cn(styles.yearButton, {[styles.yearButtonActive]: activeYear === String(currentYear)})}
                    onClick={() => applyFilter(String(currentYear))}
                >
                    {currentYear}
                </Button>
                <Button
                    type={ButtonType.third}
                    className={cn(styles.yearButton, {[styles.yearButtonActive]: activeYear === String(prevYear)})}
                    onClick={() => applyFilter(String(prevYear))}
                >
                    {prevYear}
                </Button>
                <Button
                    type={ButtonType.third}
                    className={cn(styles.filterButton, {[styles.filterButtonActive]: showMonths || activeMonth})}
                    onClick={() => setShowMonths(v => !v)}
                >
                    <Gt>Month</Gt>
                </Button>
            </Stack>

            {showMonths && (
                <MonthDropdown
                    activeYear={activeYear}
                    activeMonth={activeMonth}
                    onSelect={(month) => {
                        applyFilter(activeYear, month)
                        setShowMonths(false)
                    }}
                />
            )}
        </div>
    )
}

const StatementWide = ({statement}: { statement: StatementRow }) => {
    const navigate = useNavigate()
    return (
        <Stack direction={"column"} className={styles.payrollWide} gap={16} justifyContent={"center"}
               alignItems={"center"}>
            <Stack gap={8} direction={"column"} alignItems={"center"} justifyContent={"center"}>
                <Title_20_Coral_Primary_Regular_20>
                    <GtDatetime template={LocalDTFormater.LocalMonth}>{periodDate(statement)}</GtDatetime> <Gt>Pay slip</Gt>
                </Title_20_Coral_Primary_Regular_20>
                <Title_40_Coral_emphasis_40>
                    <GtCurrency>{statement.sashihiki_shikyugaku ?? 0}</GtCurrency>
                </Title_40_Coral_emphasis_40>
            </Stack>

            <Button type={ButtonType.third} className={styles.detailButton}
                    icon={<BookOpen size={22}/>}
                    onClick={() => navigate(`${PAGE_LINK}${statement.id}/`)}>
                <Gt>Detail</Gt>
            </Button>
        </Stack>
    )
}

export const StatementList = () => {
    const employee = useCredentialsStore(x => x.employee)
    const [params] = useSearchParams()
    const navigate = useNavigate()
    const [selected, setSelected] = useState<number | undefined>()

    const year = useMemo(() => params.get("year") ?? String(new Date().getFullYear()), [params])
    const month = useMemo(() => params.get("month") ?? undefined, [params])
    const page = useMemo(() => {
        const p = parseInt(params.get("page") ?? '1')
        return isNaN(p) ? 1 : p
    }, [params])

    const goToPage = useCallback((newPage: number) => {
        const p = new URLSearchParams(params)
        if (newPage <= 1) p.delete("page")
        else p.set("page", String(newPage))
        navigate(`?${p.toString()}`, {replace: true})
    }, [params])

    const {data, isLoading} = useQuery({
        queryKey: ['statements', employee?.id, year, month, page],
        queryFn: async () => {
            // The http client only sends the search string, never the query object.
            const searchStr = new URLSearchParams()
            searchStr.set("year", year)
            if (month) searchStr.set("month", month)
            searchStr.set("page", String(page))
            searchStr.set("page_size", String(PAGE_SIZE))

            const result = await apiClient.api.accountingStatementsList(
                undefined, {search: searchStr.toString()}
            )
            setSelected(result.data.results[0]?.id)
            return result.data
        },
        enabled: !!employee?.id,
    })

    const items = data?.results ?? []
    const totalPages = data?.total_pages ?? 1

    return (
        <Stack direction={"column"} gap={8} className={styles.root}>
            <Stack gap={8} alignItems={"center"} className={styles.filters} justifyContent={"space-between"}>
                <YearMonthFilters/>
            </Stack>

            {isLoading && (
                <Stack direction={"column"} style={{padding: 16}}>
                    <Gt>Loading</Gt>...
                </Stack>
            )}

            {!isLoading && items.length === 0 && (
                <Stack direction={"column"} style={{padding: 16}}>
                    <Gt>No data</Gt>
                </Stack>
            )}

            {items.map(statement => (
                statement.id === selected ?
                    <StatementWide statement={statement} key={statement.id}/> :
                    <Button
                        key={statement.id}
                        icon={
                            <Stack className={styles.label}>
                                <Body_16_Coral_Emphasis_16><Gt>Salary</Gt></Body_16_Coral_Emphasis_16>
                            </Stack>
                        }
                        className={styles.payroll}
                        type={ButtonType.secondary}
                        onClick={() => setSelected(statement.id)}
                    >
                        <Stack className={styles.payrollContent} gap={30} justifyContent={"space-between"}>
                            <Body_14_Coral_Primary_Regular_14>
                                <GtDatetime template={LocalDTFormater.LocalMonth}>{periodDate(statement)}</GtDatetime>
                            </Body_14_Coral_Primary_Regular_14>
                            <Body_16_Coral_Strong_emphasis_16>
                                <GtCurrency>{statement.sashihiki_shikyugaku ?? 0}</GtCurrency>
                            </Body_16_Coral_Strong_emphasis_16>
                        </Stack>
                    </Button>
            ))}

            {totalPages > 1 && (
                <Stack justifyContent={"center"} alignItems={"center"} gap={16} className={styles.pagination}>
                    <Button
                        type={ButtonType.clickableIcon}
                        icon={<ChevronLeft/>}
                        disabled={page <= 1}
                        onClick={() => goToPage(page - 1)}
                    />
                    <Body_14_Coral_Primary_Regular_14>
                        {page} / {totalPages}
                    </Body_14_Coral_Primary_Regular_14>
                    <Button
                        type={ButtonType.clickableIcon}
                        icon={<ChevronRight/>}
                        disabled={page >= totalPages}
                        onClick={() => goToPage(page + 1)}
                    />
                </Stack>
            )}
        </Stack>
    )
}
